import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';

const Vendors = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: vendorsData, isLoading } = useQuery(
    ['admin-vendors', searchQuery, statusFilter],
    () => {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      
      return api.get('/admin/vendors', { params }).then(res => res.data.data);
    },
    { keepPreviousData: true }
  );

  const approveMutation = useMutation(
    (vendorId) => api.put(`/admin/vendors/${vendorId}/approve`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-vendors');
        toast.success('تم الموافقة على البائع');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const rejectMutation = useMutation(
    (vendorId) => api.put(`/admin/vendors/${vendorId}/reject`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-vendors');
        toast.success('تم رفض البائع');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const toggleStatusMutation = useMutation(
    (vendorId) => api.put(`/admin/vendors/${vendorId}/toggle-status`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-vendors');
        toast.success('تم تحديث حالة البائع');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 ml-1" />
          معتمد
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          <ClockIcon className="w-4 h-4 ml-1" />
          قيد المراجعة
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
        <XCircleIcon className="w-4 h-4 ml-1" />
        مرفوض
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">إدارة البائعين</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث باسم المتجر..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">جميع الحالات</option>
              <option value="pending">قيد المراجعة</option>
              <option value="approved">معتمد</option>
              <option value="rejected">مرفوض</option>
            </select>
          </div>
        </div>

        {/* Vendors Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    المتجر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    صاحب المتجر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الفئة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    النشاط
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vendorsData?.data?.map((vendor) => (
                  <tr key={vendor.id}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <BuildingStorefrontIcon className="w-10 h-10 text-gray-400 ml-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{vendor.store_name}</div>
                          <div className="text-sm text-gray-500">{vendor.store_slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{vendor.user?.name}</div>
                      <div className="text-sm text-gray-500">{vendor.user?.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {vendor.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(vendor.status)}
                    </td>
                    <td className="px-6 py-4">
                      {vendor.is_active ? (
                        <span className="text-green-600 text-sm">نشط</span>
                      ) : (
                        <span className="text-red-600 text-sm">معطل</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2 space-x-reverse">
                        {vendor.status === 'pending' && (
                          <>
                            <button
                              onClick={() => approveMutation.mutate(vendor.id)}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                            >
                              موافقة
                            </button>
                            <button
                              onClick={() => rejectMutation.mutate(vendor.id)}
                              className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                            >
                              رفض
                            </button>
                          </>
                        )}
                        {vendor.status === 'approved' && (
                          <button
                            onClick={() => toggleStatusMutation.mutate(vendor.id)}
                            className={`px-3 py-1 text-sm rounded-md ${
                              vendor.is_active
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {vendor.is_active ? 'تعطيل' : 'تفعيل'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default Vendors;
